import { GridLogicOperator, SearchMode } from '@warehouse/object-browser/core';
import { ObjectBrowserLink, ObjectBrowserLinkBuilder, tableManagerStoreConfig } from '@warehouse/object-browser/domain';
import { encodeUrlParam } from '@warehouse/shared/util';

/**
 * The Object Browser links of the Service Log's ETag column: the same thing a user typing the eTag in its search
 * gets. The Object Browser restores its search and table state from session storage whenever the URL leaves them out,
 * so a link spells out everything that decides what it shows: the search, its scope, the filters and the columns.
 */
export const ObjectBrowserDeepLinks = {
	/**
	 * Every copy of that content, in every bucket (All Storage), with the ETag column shown since the wildcard search
	 * only looks in visible columns. No filters, not even the default online one: an offline copy is still a copy.
	 * `showOffline` (a Delete job, whose copies went offline) also shows the Offline column, and spells out its filter
	 * as "any" so the filter panel says offline copies are included.
	 */
	forETag({
		bucketId,
		eTag,
		showOffline = false,
	}: {
		bucketId: string;
		eTag: string;
		showOffline?: boolean;
	}): ObjectBrowserLink {
		const { pathname, search } = ObjectBrowserLinkBuilder.buildBucketLink({ bucketId });
		const params = new URLSearchParams(search);
		params.set('searchQuery', eTag);
		params.set('searchMode', SearchMode.EVERYWHERE);
		params.set(
			'filters',
			encodeUrlParam({
				// No value: MUI X's "any", which the Object Browser does not send to the Inventory.
				items: showOffline ? [{ field: 'online', operator: 'is' }] : [],
				logicOperator: GridLogicOperator.AND,
			}),
		);
		params.set(
			'column-visibility',
			encodeUrlParam({
				...tableManagerStoreConfig('').visibility?.defaultValue,
				eTag: true,
				...(showOffline && { online: true }),
			}),
		);
		return { pathname, search: `?${params.toString()}` };
	},
};
